import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Gauge, CheckCircle, AlertTriangle, XCircle, Clock, FileText,
  ArrowRight, List, Calendar, Shield, Activity, Download, Upload, Settings,
  Plus, Trash2, Mail, Phone
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const STATUS_CONFIG = {
  valid: { label: 'Valid', color: '#10b981', bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: CheckCircle },
  atentie: { label: 'Atentie', color: '#f59e0b', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: Clock },
  expira_curand: { label: 'Expira curand', color: '#f97316', bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', icon: AlertTriangle },
  expirat: { label: 'Expirat', color: '#ef4444', bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: XCircle },
  necunoscut: { label: 'Necunoscut', color: '#94a3b8', bg: 'bg-slate-50 border-slate-200', text: 'text-slate-500', icon: Clock }
};

const EMMDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    responsabil_nume: '', responsabil_functie: 'Responsabil Metrolog',
    alerte_enabled: true, alerte_zile_inainte: 90, alerte_email: false,
    destinatari: []
  });
  const [newDest, setNewDest] = useState({ name: '', email: '', phone: '' });

  useEffect(() => { fetchStats(); fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/emm/settings');
      setSettings(res.data);
    } catch {}
  };

  const handleSaveSettings = async () => {
    try {
      let toSave = { ...settings };
      if (newDest.name && (newDest.email || newDest.phone)) {
        toSave = { ...toSave, destinatari: [...(toSave.destinatari || []), { ...newDest }] };
        setSettings(toSave);
        setNewDest({ name: '', email: '', phone: '' });
      }
      await api.post('/emm/settings', toSave);
      toast.success('Setari EMM salvate!');
      setShowSettings(false);
    } catch { toast.error('Eroare la salvare'); }
  };

  const addDestinatarEMM = () => {
    if (!newDest.name || (!newDest.email && !newDest.phone)) {
      toast.error('Completati numele si cel putin email sau telefon'); return;
    }
    setSettings(p => ({ ...p, destinatari: [...(p.destinatari || []), { ...newDest }] }));
    setNewDest({ name: '', email: '', phone: '' });
  };

  const removeDestinatarEMM = (idx) => {
    setSettings(p => ({ ...p, destinatari: (p.destinatari || []).filter((_, i) => i !== idx) }));
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/emm/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor EMM');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
      </div>
    );
  }

  const kpiCards = [
    { label: 'Total EMM Active', value: stats.total, color: 'bg-blue-500', icon: Gauge },
    { label: 'Valide', value: stats.valid, color: 'bg-emerald-500', icon: CheckCircle },
    { label: 'Atentie (< 90 zile)', value: stats.atentie, color: 'bg-amber-500', icon: Clock },
    { label: 'Expira Curand (< 30 zile)', value: stats.expira_curand, color: 'bg-orange-500', icon: AlertTriangle },
    { label: 'Expirate', value: stats.expirat, color: 'bg-red-500', icon: XCircle }
  ];

  // Pie chart data
  const pieData = [
    { name: 'Valide', value: stats.valid, color: '#10b981' },
    { name: 'Atentie', value: stats.atentie, color: '#f59e0b' },
    { name: 'Expira curand', value: stats.expira_curand, color: '#f97316' },
    { name: 'Expirate', value: stats.expirat, color: '#ef4444' },
    { name: 'Necunoscut', value: stats.necunoscut, color: '#94a3b8' },
  ].filter(d => d.value > 0);

  const validPercent = stats.total > 0 ? Math.round((stats.valid / stats.total) * 100) : 0;
  const equipmentList = stats.equipment_list || [];

  // Next expiration
  const nextExpiring = equipmentList.find(e => e.days_until_expiry !== null && e.days_until_expiry > 0);

  return (
    <div className="space-y-6" data-testid="emm-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">EMM-uri</h1>
          <p className="text-slate-600 mt-1">Echipamente de Masurare si Monitorizare</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="h-12" onClick={() => setShowSettings(true)} data-testid="emm-settings-btn">
            <Settings className="w-5 h-5 mr-2" />
            Setari EMM
          </Button>
          <Button variant="outline" className="h-12" onClick={() => navigate('/emm/nomenclator')} data-testid="go-to-nomenclator-btn">
            <List className="w-5 h-5 mr-2" />
            Nomenclator
          </Button>
          <Button className="btn-primary h-12" onClick={() => navigate('/emm/nomenclator')} data-testid="go-to-pdf-btn">
            <FileText className="w-5 h-5 mr-2" />
            Genereaza Lista PDF
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {kpiCards.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <Card key={idx} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`kpi-card-${idx}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${kpi.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{kpi.value}</p>
                    <p className="text-xs text-slate-500">{kpi.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Middle section: Pie Chart + Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pie Chart */}
        <Card className="border-0 shadow-sm" data-testid="emm-pie-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Distributie Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} echipamente`, name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{validPercent}%</p>
                  <p className="text-xs text-slate-500">conforme</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-4 justify-center">
              {pieData.map((d, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                  <span className="text-slate-600">{d.name} ({d.value})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sumar & Informatii rapide */}
        <Card className="border-0 shadow-sm" data-testid="emm-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Sumar Verificari
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress bar for compliance */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Rata conformitate</span>
                <span className="font-semibold text-slate-900">{validPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div className="bg-emerald-500 h-3 rounded-full transition-all duration-500"
                     style={{ width: `${validPercent}%` }} />
              </div>
            </div>

            {/* Stats rows */}
            <div className="space-y-2.5">
              {[
                { label: 'Echipamente verificate la zi', value: stats.valid, total: stats.total, color: 'text-emerald-600' },
                { label: 'Necesita atentie (< 90 zile)', value: stats.atentie, total: stats.total, color: 'text-amber-600' },
                { label: 'Expira curand (< 30 zile)', value: stats.expira_curand, total: stats.total, color: 'text-orange-600' },
                { label: 'Expirate', value: stats.expirat, total: stats.total, color: 'text-red-600' },
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-600">{row.label}</span>
                  <span className={`text-sm font-semibold ${row.color}`}>{row.value} / {row.total}</span>
                </div>
              ))}
            </div>

            {/* Next expiration */}
            {nextExpiring && (
              <div className="bg-blue-50 rounded-lg p-3 mt-2">
                <p className="text-xs text-blue-600 font-medium">Urmatoarea verificare expira:</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">{nextExpiring.denumire}</p>
                <p className="text-xs text-slate-500">
                  {nextExpiring.verificat_pana_la ? new Date(nextExpiring.verificat_pana_la).toLocaleDateString('ro-RO') : ''} ({nextExpiring.days_until_expiry} zile)
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm" data-testid="emm-quick-actions">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Actiuni Rapide
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button variant="outline" className="w-full justify-start h-11 text-left"
                    onClick={() => navigate('/emm/nomenclator')}>
              <List className="w-4 h-4 mr-3 text-blue-600" />
              <div>
                <p className="text-sm font-medium">Nomenclator EMM</p>
                <p className="text-xs text-slate-400">{stats.total} echipamente inregistrate</p>
              </div>
            </Button>
            <Button variant="outline" className="w-full justify-start h-11 text-left"
                    onClick={() => navigate('/emm/nomenclator')}>
              <FileText className="w-4 h-4 mr-3 text-red-600" />
              <div>
                <p className="text-sm font-medium">Genereaza Lista PDF</p>
                <p className="text-xs text-slate-400">Format A4, antet ADF, semnatura</p>
              </div>
            </Button>
            <Button variant="outline" className="w-full justify-start h-11 text-left"
                    onClick={() => navigate('/emm/nomenclator')}>
              <Download className="w-4 h-4 mr-3 text-emerald-600" />
              <div>
                <p className="text-sm font-medium">Export Excel</p>
                <p className="text-xs text-slate-400">Descarca nomenclator complet</p>
              </div>
            </Button>
            <Button variant="outline" className="w-full justify-start h-11 text-left"
                    onClick={() => navigate('/emm/nomenclator')}>
              <Upload className="w-4 h-4 mr-3 text-blue-600" />
              <div>
                <p className="text-sm font-medium">Import Excel</p>
                <p className="text-xs text-slate-400">Incarca echipamente din fisier</p>
              </div>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {stats.alerts.length > 0 && (
        <Card className="border-red-200 shadow-sm" data-testid="emm-alerts-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Alerte Verificare ({stats.alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.alerts.map((alert, idx) => {
                const cfg = STATUS_CONFIG[alert.verification_status] || STATUS_CONFIG.necunoscut;
                const AlertIcon = cfg.icon;
                return (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${cfg.bg}`} data-testid={`alert-${idx}`}>
                    <div className="flex items-center gap-3">
                      <AlertIcon className={`w-5 h-5 ${cfg.text}`} />
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{alert.denumire}</p>
                        <p className="text-xs text-slate-500">
                          Verificat pana la: {alert.verificat_pana_la ? new Date(alert.verificat_pana_la).toLocaleDateString('ro-RO') : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <Badge className={`${alert.days_until_expiry < 0 ? 'bg-red-500' : alert.days_until_expiry <= 30 ? 'bg-orange-500' : 'bg-amber-500'} text-white`}>
                      {alert.days_until_expiry < 0 ? `Expirat de ${Math.abs(alert.days_until_expiry)} zile` : `${alert.days_until_expiry} zile ramase`}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Equipment Table */}
      <Card className="border-0 shadow-sm" data-testid="emm-equipment-table">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Gauge className="w-5 h-5 text-blue-600" />
              Toate Echipamentele EMM ({equipmentList.length})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/emm/nomenclator')}
                    className="text-blue-600 hover:text-blue-700">
              Vezi detalii <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="emm-table">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="pb-2 pr-4 text-slate-500 font-medium text-xs">Nr.</th>
                  <th className="pb-2 pr-4 text-slate-500 font-medium text-xs">Denumire Echipament</th>
                  <th className="pb-2 pr-4 text-slate-500 font-medium text-xs">Tip / Seria</th>
                  <th className="pb-2 pr-4 text-slate-500 font-medium text-xs">Verificat la</th>
                  <th className="pb-2 pr-4 text-slate-500 font-medium text-xs">Verificat pana la</th>
                  <th className="pb-2 pr-4 text-slate-500 font-medium text-xs">Interval</th>
                  <th className="pb-2 text-slate-500 font-medium text-xs">Status</th>
                </tr>
              </thead>
              <tbody>
                {equipmentList.map((item, idx) => {
                  const cfg = STATUS_CONFIG[item.verification_status] || STATUS_CONFIG.necunoscut;
                  return (
                    <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`equip-row-${idx}`}>
                      <td className="py-2.5 pr-4 text-slate-400 text-xs">{idx + 1}</td>
                      <td className="py-2.5 pr-4 font-medium text-slate-900 text-xs">{item.denumire}</td>
                      <td className="py-2.5 pr-4 text-slate-600 text-xs">{item.tip_serie}</td>
                      <td className="py-2.5 pr-4 text-slate-600 text-xs">
                        {item.verificat_la ? new Date(item.verificat_la).toLocaleDateString('ro-RO') : '-'}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-600 text-xs">
                        {item.verificat_pana_la ? new Date(item.verificat_pana_la).toLocaleDateString('ro-RO') : '-'}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-600 text-xs">{item.interval_verificare}</td>
                      <td className="py-2.5">
                        <Badge className={`${cfg.bg} ${cfg.text} text-xs border`}>{cfg.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              Setari EMM
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-slate-700">Responsabil Metrolog (semnatura PDF)</p>
              <p className="text-xs text-slate-400">Numele si functia apar automat in lista PDF generata</p>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Nume complet *</Label>
                <Input
                  value={settings.responsabil_nume}
                  onChange={e => setSettings(p => ({ ...p, responsabil_nume: e.target.value }))}
                  placeholder="ex: DUTU VALENTIN"
                  className="h-11"
                  data-testid="emm-settings-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Functia</Label>
                <Input
                  value={settings.responsabil_functie}
                  onChange={e => setSettings(p => ({ ...p, responsabil_functie: e.target.value }))}
                  placeholder="ex: Responsabil Metrolog"
                  className="h-11"
                  data-testid="emm-settings-function"
                />
              </div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-semibold text-slate-700">Alerte Verificare</p>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm">Alerte active</p>
                  <p className="text-xs text-slate-400">Notificari pentru echipamente care expira</p>
                </div>
                <Switch
                  checked={settings.alerte_enabled}
                  onCheckedChange={v => setSettings(p => ({ ...p, alerte_enabled: v }))}
                  data-testid="emm-settings-alerts-toggle"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Alertare cu zile inainte de expirare</Label>
                <Input
                  type="number"
                  value={settings.alerte_zile_inainte}
                  onChange={e => setSettings(p => ({ ...p, alerte_zile_inainte: parseInt(e.target.value) || 90 }))}
                  className="h-11"
                  min={1}
                  data-testid="emm-settings-alert-days"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm">Alerte pe email</p>
                  <p className="text-xs text-slate-400">Trimite email cand un echipament expira</p>
                </div>
                <Switch
                  checked={settings.alerte_email}
                  onCheckedChange={v => setSettings(p => ({ ...p, alerte_email: v }))}
                  data-testid="emm-settings-email-toggle"
                />
              </div>
            </div>

            {/* Destinatari */}
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-semibold text-slate-700">Destinatari Alerte ({(settings.destinatari || []).length})</p>
              {(settings.destinatari || []).length > 0 && (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {(settings.destinatari || []).map((d, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-sm">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900">{d.name}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          {d.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{d.email}</span>}
                          {d.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{d.phone}</span>}
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => removeDestinatarEMM(i)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Nume" value={newDest.name} onChange={e => setNewDest(p => ({ ...p, name: e.target.value }))} className="h-9 text-sm" data-testid="emm-dest-name" />
                  <Input placeholder="Email" value={newDest.email} onChange={e => setNewDest(p => ({ ...p, email: e.target.value }))} className="h-9 text-sm" data-testid="emm-dest-email" />
                  <div className="flex gap-1">
                    <Input placeholder="Telefon" value={newDest.phone} onChange={e => setNewDest(p => ({ ...p, phone: e.target.value }))} className="h-9 text-sm" data-testid="emm-dest-phone" />
                    <Button size="icon" variant="outline" className="h-9 w-9 flex-shrink-0" onClick={addDestinatarEMM} data-testid="emm-add-dest-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Anuleaza</Button>
            <Button className="btn-primary" onClick={handleSaveSettings} data-testid="save-emm-settings-btn">Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empty state */}
      {stats.total === 0 && (
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Gauge className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">Nu exista echipamente EMM inregistrate</p>
            <Button className="btn-primary" onClick={() => navigate('/emm/nomenclator')}>
              <ArrowRight className="w-4 h-4 mr-2" />
              Adauga Primul Echipament
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EMMDashboardPage;
